import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import {
  BrowserControlCommunicationService,
  GmSltService,
  ServiceVisitRecordService,
} from '@applications/sha-services';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { HyundaiMenuNgrxFacade } from '@quorum/hyundai-menu-ngrx';
import { GmSltData, ServiceVisitRecord } from '@quorum/models/xs-misc';
import { QuoteDetailQueryParameters } from '@quorum/models/xs-query';
import {
  AppointmentCase,
  Employee,
  QuoteDetail,
  Service,
  ServiceAppointmentDeclinedMpviCode,
  WorkOrder,
  WorkOrderCase,
  WorkorderDeclinedMpviCode,
} from '@quorum/models/xs-resource';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'sha-menu-shell',
  templateUrl: './menu-shell.component.html',
  styleUrls: ['./menu-shell.component.scss'],
})
export class MenuShellComponent implements OnInit, OnDestroy {
  params: Params;
  oem: string;
  declinedWorkOrderMpviSub$: Subscription;
  declinedAppointmentMpviSub$: Subscription;
  removeAcceptedServiceFromMenuSubscription$: Subscription;
  updateDeclinedServiceFromMenuSubscription$: Subscription;
  removeDeclinedServiceFromMenuSubscription$: Subscription;

  workOrderCaseData$: Subscription;
  appointmentCaseData$: Subscription;
  quoteDetailData$: Subscription;

  workOrderCases: WorkOrderCase[] = [];
  appointmentCases: AppointmentCase[] = [];
  declinedWorkOrderMpvis: WorkorderDeclinedMpviCode[] = [];
  declinedAppointmentMpvis: WorkorderDeclinedMpviCode[] = [];

  workOrder: WorkOrder;

  acceptMenuSub$: Subscription;
  declineMenuSub$: Subscription;

  authEmployee: Employee;

  destroy$ = new Subject();
  gmSltData$: Observable<GmSltData>;
  serviceVisitRecord$: Observable<ServiceVisitRecord>;

  constructor(
    public serviceLaneFacade: ServiceLaneFacade,
    public browserControlService: BrowserControlCommunicationService,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private authenticationStateService: AuthenticationStateService,
    private hyundaiMenuFacade: HyundaiMenuNgrxFacade,
    private gmSltService: GmSltService,
    private serviceVisitRecordService: ServiceVisitRecordService
  ) {}

  ngOnDestroy() {
    if (this.acceptMenuSub$) {
      this.acceptMenuSub$.unsubscribe();
    }
    if (this.declineMenuSub$) {
      this.declineMenuSub$.unsubscribe();
    }
    if (this.removeAcceptedServiceFromMenuSubscription$) {
      this.removeAcceptedServiceFromMenuSubscription$.unsubscribe();
    }
    if (this.updateDeclinedServiceFromMenuSubscription$) {
      this.updateDeclinedServiceFromMenuSubscription$.unsubscribe();
    }
    if (this.removeDeclinedServiceFromMenuSubscription$) {
      this.removeDeclinedServiceFromMenuSubscription$.unsubscribe();
    }
    if (this.workOrderCaseData$) {
      this.workOrderCaseData$.unsubscribe();
    }
    if (this.appointmentCaseData$) {
      this.appointmentCaseData$.unsubscribe();
    }
    if (this.declinedWorkOrderMpviSub$) {
      this.declinedWorkOrderMpviSub$.unsubscribe();
    }
    if (this.declinedAppointmentMpviSub$) {
      this.declinedAppointmentMpviSub$.unsubscribe();
    }
    if (this.quoteDetailData$) {
      this.quoteDetailData$.unsubscribe();
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  // Query params are never null (absent is undefined), and XS sends 0 for "no transaction".
  private get hasRouteTransactionId(): boolean {
    return Number(this.route.snapshot.queryParams.transactionId) > 0;
  }

  ngOnInit(): void {
    if (
      this.route.snapshot.queryParams.transactionType === 'workOrder' &&
      this.hasRouteTransactionId &&
      this.route.snapshot.queryParams.oem === 'GM' &&
      this.browserControlService.embeddedInXsellerator
    ) {
      this.gmSltData$ = this.authenticationStateService.selectAuthenticatedUser().pipe(
        filter((authenticatedUser) => authenticatedUser !== null),
        map((authUser) => authUser.user),
        switchMap((authenticatedUser) => {
          return this.gmSltService.getOrCreateGmSltData(
            authenticatedUser.destination,
            this.route.snapshot.queryParams.transactionId
          );
        })
      );
    }

    const { transactionType, transactionId } = this.route.snapshot.queryParams;

    const isAppointmentType =
      transactionType === 'appointment' || transactionType === 'serviceAppointment' || transactionType === 'SA';

    const isWorkOrderType = transactionType === 'workOrder';

    if (transactionId && (isAppointmentType || isWorkOrderType)) {
      this.serviceVisitRecord$ = this.authenticationStateService.selectAuthenticatedUser().pipe(
        filter((authenticatedUser) => authenticatedUser !== null),
        take(1),
        map((authUser) => authUser.user),
        switchMap((authenticatedUser) => {
          return this.serviceVisitRecordService.getOrCreateServiceVisitRecord(
            authenticatedUser.destination,
            isAppointmentType ? transactionId : undefined,
            isWorkOrderType ? transactionId : undefined
          );
        }),
        filter((svr): svr is ServiceVisitRecord => !!svr),
        shareReplay(1),
        switchMap((svr: ServiceVisitRecord) => {
          if (svr.communications?.advisorMobileCheckIn) {
            return of(svr);
          }
          return this.serviceVisitRecordService.recordAdvisorMobileCheckIn(svr);
        }),
        switchMap((svr: ServiceVisitRecord) => {
          return this.serviceVisitRecordService.recordInitialSnapshot(svr);
        })
      );
      this.serviceVisitRecord$.pipe(takeUntil(this.destroy$)).subscribe();
    }

    if (this.route.snapshot.queryParams) {
      this.params = this.route.snapshot.queryParams;
    }
    this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(
        filter((employee) => employee != null),
        take(1),
        tap((authEmployee) => {
          this.authEmployee = authEmployee;
        })
      )
      .subscribe();

    /**
     * Adding OEM check for GM here to address XS-2289, additional work may be required to
     * handle the large number of scenarios where duplicate quote lines/cases can get added via XS
     */
    if (
      this.route.snapshot.queryParams.transactionType === 'workOrder' &&
      this.hasRouteTransactionId &&
      this.route.snapshot.queryParams.oem !== 'GM'
    ) {
      this.workOrderCaseData$ = combineLatest([
        this.apiService.get<WorkOrderCase[]>(
          `v/1/fixed-operations/work-orders/${this.route.snapshot.queryParams.transactionId}/cases`
        ),
        this.apiService.get<WorkorderDeclinedMpviCode[]>(`v/1/fixed-operations/work-order-declined-mpvi-codes`, {
          params: { workOrderId: this.route.snapshot.queryParams.transactionId },
        }),
        this.apiService
          .get<WorkOrder[]>(`v/1/fixed-operations/work-orders/${this.route.snapshot.queryParams.transactionId}`)
          .pipe(
            tap((workOrder) => {
              this.workOrder = workOrder;
            })
          ),
      ])
        .pipe(
          map(([workOrderCases, declinedWorkOrderMpvis]) => {
            return { workOrderCases, declinedWorkOrderMpvis };
          })
        )
        .subscribe((data) => {
          const acceptedServicesOnWorkOrderWithStatus: any = [];
          const declinedServicesOnWorkOrderWithDeclinedReason: any = [];

          data.declinedWorkOrderMpvis.forEach((workorderDeclinedMpviCode: WorkorderDeclinedMpviCode) => {
            declinedServicesOnWorkOrderWithDeclinedReason.push({
              id: workorderDeclinedMpviCode.opCode,
              declinedReasonId: workorderDeclinedMpviCode.declinedReasonId,
            });
          });

          switch (this.route.snapshot.queryParams.oem) {
            case 'HYU':
              data.workOrderCases.forEach((workOrderCase: WorkOrderCase) => {
                acceptedServicesOnWorkOrderWithStatus.push({
                  id: workOrderCase.serviceGroupId,
                  status: workOrderCase.caseStatusId,
                });
              });
              this.hyundaiMenuFacade.updateServicesForHyundaiMenu(
                acceptedServicesOnWorkOrderWithStatus,
                declinedServicesOnWorkOrderWithDeclinedReason
              );
              break;
          }
        });
    }

    /**
     * Adding OEM check for GM here to address XS-2289, additional work may be required to
     * handle the large number of scenarios where duplicate quote lines/cases can get added via XS
     */
    if (
      (this.route.snapshot.queryParams.transactionType === 'appointment' ||
        this.route.snapshot.queryParams.transactionType === 'serviceAppointment') &&
      this.hasRouteTransactionId &&
      this.route.snapshot.queryParams.oem !== 'GM'
    ) {
      this.appointmentCaseData$ = combineLatest([
        this.apiService.get<AppointmentCase[]>(`v/1/fixed-operations/appointment-cases`, {
          params: { appointmentId: this.route.snapshot.queryParams.transactionId },
        }),
        this.apiService.get<ServiceAppointmentDeclinedMpviCode[]>(
          `v/1/fixed-operations/service-appointment-declined-mpvi-codes`,
          {
            params: { appointmentId: this.route.snapshot.queryParams.transactionId },
          }
        ),
      ])
        .pipe(
          map(([appointmentCases, declinedAppointmentMpvis]) => {
            return { appointmentCases, declinedAppointmentMpvis };
          })
        )
        .subscribe((data) => {
          const acceptedServicesOnAppointmentWithStatus: any = [];
          const declinedServicesOnAppointmentWithDeclinedReason: any = [];

          data.declinedAppointmentMpvis.forEach(
            (serviceAppointmentDeclinedMpviCode: ServiceAppointmentDeclinedMpviCode) => {
              declinedServicesOnAppointmentWithDeclinedReason.push({
                id: serviceAppointmentDeclinedMpviCode.opCode,
                declinedReasonId: serviceAppointmentDeclinedMpviCode.declinedReason,
              });
            }
          );

          switch (this.route.snapshot.queryParams.oem) {
            case 'HYU':
              data.appointmentCases.forEach((appointmentCase: AppointmentCase) => {
                acceptedServicesOnAppointmentWithStatus.push({
                  id: appointmentCase.serviceGroupId,
                  status: null,
                });
              });
              this.hyundaiMenuFacade.updateServicesForHyundaiMenu(
                acceptedServicesOnAppointmentWithStatus,
                declinedServicesOnAppointmentWithDeclinedReason
              );
              break;
          }
        });
    }

    if (
      this.route.snapshot.queryParams.transactionType === 'quote' &&
      this.hasRouteTransactionId &&
      this.route.snapshot.queryParams.oem !== 'GM'
    ) {
      const quoteDetailQueryParams: QuoteDetailQueryParameters = new QuoteDetailQueryParameters({
        quoteHeaderId: this.route.snapshot.queryParams.transactionId.toString(),
        embed: 'employeeAssociate,serviceGroup',
      });

      this.serviceLaneFacade.getQuoteLinesFromServer(quoteDetailQueryParams);

      /**
       * TODO: Add code here to automatically set the accept/decline button status if the service already exists on the quote?
       */
    }

    switch (this.route.snapshot.queryParams.oem) {
      case 'HYU':
        this.prepareHyundaiObservables();
        break;
    }
  }

  prepareHyundaiObservables() {
    this.acceptMenuSub$ = this.serviceLaneFacade.acceptServiceFromHyundaiMenu$
      .pipe(
        filter((hyundaiMenuData) => hyundaiMenuData != null),
        switchMap((hyundaiMenuData) => {
          this.serviceLaneFacade.disableHyundaiMenuRemoveServiceButton$.next(true);
          const serviceIds: string = hyundaiMenuData?.serviceGroup.services
            .map((service: any) => service.serviceId)
            .join();
          return this.apiService.get<Service[]>(`v/1/fixed-operations/services`, { params: { id: serviceIds } }).pipe(
            map((services) => {
              return { hyundaiMenuData, services };
            })
          );
        }),
        map((data) => {
          return {
            serviceGroup: data.hyundaiMenuData.serviceGroup,
            vehicle: data.hyundaiMenuData.vehicle,
            services: data.services,
          };
        }),
        tap((data) => {
          if (this.params.transactionType === 'workOrder') {
            const workOrderCase = new WorkOrderCase();
            workOrderCase.workOrderId = this.params.transactionId;

            workOrderCase.addedFromServiceSchedule = true;
            workOrderCase.allowMultipleTechFlag = '0';
            workOrderCase.approvedFlag = '1';
            workOrderCase.caseStatusId = 'N';
            workOrderCase.chargeoutRateBasisId = 1;
            workOrderCase.employeeId = this.authEmployee.associateId;
            workOrderCase.estimatedTime = data.serviceGroup.estimatedTime;

            workOrderCase.internalTransTypeId = null;
            workOrderCase.labourOp = data.serviceGroup.workOrderLocationOpCode;
            workOrderCase.labourOpFlag = '1';
            if (this.workOrder.requestSymptomsPrintedFlag == '1') {
              workOrderCase.note = 'Added Operation: ';
            } else {
              workOrderCase.note = '';
            }
            workOrderCase.note += data.serviceGroup.name + ' (';
            data.services.forEach((service: any) => {
              workOrderCase.note += ' ' + service?.name;
            });
            workOrderCase.note += ')';

            workOrderCase.recommendedHours = data.serviceGroup.serviceGroupRateId
              ? data.serviceGroup.serviceGroupRate.recommendedHours != null
                ? data.serviceGroup.serviceGroupRate.recommendedHours
                : data.serviceGroup.recommendedHours
              : data.serviceGroup.recommendedHours;

            workOrderCase.serviceGroupId = data.serviceGroup.id;
            workOrderCase.shopSupplyFlag = '1';
            workOrderCase.technicianGroupId = data.serviceGroup.technicianGroupId;
            workOrderCase.technicianGroupName = data.serviceGroup.technicianGroupName;
            workOrderCase.technicianId = data.serviceGroup.technicianId;
            workOrderCase.technicianRateBasisId = 1;

            workOrderCase.warrantySubmissionTypeId =
              data.serviceGroup.warrantySubmissionTypeId != null &&
              data.serviceGroup.warrantySubmissionTypeId.trim() != ''
                ? data.serviceGroup.warrantySubmissionTypeId
                : 'CP';

            this.serviceLaneFacade.createWorkOrderCaseOnServer(
              workOrderCase,
              { serviceGroup: data.serviceGroup },
              data.vehicle,
              this.workOrder
            );
          } else if (this.params.transactionType === 'appointment') {
            const appointmentCase: AppointmentCase = new AppointmentCase();
            appointmentCase.appointmentId = this.params.transactionId;
            appointmentCase.employeeId = this.authEmployee.associateId;
            appointmentCase.opCode = data.serviceGroup.workOrderLocationOpCode;
            //appointmentCase.quoteCaseNumber = quoteLine.sequence;
            appointmentCase.recommendedHours = data.serviceGroup.recommendedHours
              ? data.serviceGroup.recommendedHours
              : 0;
            appointmentCase.estimatedTime = data.serviceGroup.estimatedTime ? data.serviceGroup.estimatedTime : 0;
            appointmentCase.warrantySubmissionTypeId = data.serviceGroup.warrantySubmissionTypeId;

            appointmentCase.requestDescription = data.serviceGroup.name;
            appointmentCase.serviceGroupId = data.serviceGroup.id;
            appointmentCase.caseFromQuote = true;
            appointmentCase.technicianId = data.serviceGroup.technicianId;
            appointmentCase.technicianGroupId = data.serviceGroup.technicianGroupId;
            appointmentCase.technicianGroupName = data.serviceGroup.technicianGroupName;

            this.serviceLaneFacade.createAppointmentCaseOnServer(appointmentCase, null);
          } else if (this.params.transactionType === 'quote') {
            this.serviceLaneFacade
              .activeQuoteLineWithServiceGroupExists(data.serviceGroup.id)
              .pipe(take(1))
              .subscribe((activeQuoteLine) => {
                let quoteLineRate: number = null;
                if (data.serviceGroup.serviceGroupRate) {
                  if (data.serviceGroup?.serviceGroupRate?.rateType === '2') {
                    quoteLineRate = data?.serviceGroup?.serviceGroupRate?.rate;
                  } else {
                    quoteLineRate = data?.serviceGroup?.serviceGroupRate?.labourRate;
                  }
                }

                if (!activeQuoteLine) {
                  const quoteDetail: QuoteDetail = {
                    ...new QuoteDetail(),
                    rooftopId: this.authEmployee.rooftopId,
                    employeeId: this.authEmployee.associateId,
                    associateId: this.authEmployee.associateId,
                    serviceGroupId: data.serviceGroup.id,
                    serviceName: data.serviceGroup.name,
                    labourOpCode: data.serviceGroup.workOrderLocationOpCode,
                    labourSubTotal: data.serviceGroup.serviceGroupRate
                      ? data?.serviceGroup?.serviceGroupRate?.labourRate
                        ? data?.serviceGroup?.serviceGroupRate?.labourRate
                        : null
                      : null,
                    recommendedHours: data.serviceGroup.serviceGroupRateId
                      ? data.serviceGroup.serviceGroupRate.recommendedHours != null
                        ? data.serviceGroup.serviceGroupRate.recommendedHours
                        : data.serviceGroup.recommendedHours
                      : data.serviceGroup.recommendedHours,
                    technicianGroupId: data.serviceGroup.technicianGroupId,
                    technicianGroupName: data.serviceGroup.technicianGroupName,
                    technicianId: data.serviceGroup.technicianId,
                    warrantySubmissionTypeId:
                      data.serviceGroup.warrantySubmissionTypeId != null &&
                      data.serviceGroup.warrantySubmissionTypeId.trim() != ''
                        ? data.serviceGroup.warrantySubmissionTypeId
                        : 'CP',
                    rateType: data.serviceGroup.serviceGroupRate
                      ? data.serviceGroup?.serviceGroupRate?.rateType
                        ? data?.serviceGroup?.serviceGroupRate?.rateType
                        : null
                      : null,
                    rate: quoteLineRate,
                    shopSupplyFlag: data?.serviceGroup?.serviceGroupRate
                      ? data?.serviceGroup?.serviceGroupRate?.shopFlag
                        ? data?.serviceGroup?.serviceGroupRate?.shopFlag
                        : null
                      : null,
                    totalAmount: data?.serviceGroup?.serviceGroupRate
                      ? data?.serviceGroup?.serviceGroupRate?.rate
                        ? data?.serviceGroup?.serviceGroupRate?.rate
                        : null
                      : null,
                    lineAddedFromServiceSchedule: 1,
                    quoteHeaderId: this.params.transactionId,
                  };
                  (quoteDetail.excludeFromVcr = ['CP', 'P2', 'P3', 'P4'].includes(quoteDetail.warrantySubmissionTypeId)
                    ? false
                    : true),
                    (quoteDetail.expirationDate = new Date());
                  quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);
                  this.serviceLaneFacade.createQuoteLineOnServer(quoteDetail);
                } else {
                  /**
                   * If the line was previously declined, switch to accepted by removing declined fields
                   */
                  if (activeQuoteLine.declined) {
                    activeQuoteLine.declined = null;
                    activeQuoteLine.declinedDatetime = null;
                    this.serviceLaneFacade.updateQuoteLineOnServer(activeQuoteLine);
                  } else {
                    this.serviceLaneFacade.disableHyundaiMenuRemoveServiceButton$.next(false);
                  }
                }
              });
          }
        })
      )
      .subscribe();

    this.removeAcceptedServiceFromMenuSubscription$ = this.serviceLaneFacade.removeAcceptedServiceFromHyundaiMenu$
      .pipe(
        switchMap((serviceGroup) => {
          if (this.params.transactionType === 'workOrder') {
            return this.apiService
              .get<WorkOrderCase[]>(
                `v/1/fixed-operations/work-orders/${this.route.snapshot.queryParams.transactionId}/cases`
              )
              .pipe(
                map(
                  (workOrderCases) =>
                    workOrderCases.filter((workOrderCase: any) => workOrderCase.serviceGroupId === serviceGroup)[0]
                ),
                take(1)
              );
          } else if (this.params.transactionType === 'appointment') {
            return this.apiService
              .get<AppointmentCase[]>(`v/1/fixed-operations/appointment-cases`, {
                params: { appointmentId: this.route.snapshot.queryParams.transactionId },
              })
              .pipe(
                map(
                  (appointmentCases) =>
                    appointmentCases.filter(
                      (appointmentCase: any) => appointmentCase.serviceGroupId === serviceGroup
                    )[0]
                ),
                take(1)
              );
          } else if (this.params.transactionType === 'quote') {
            const quoteDetailQueryParams: QuoteDetailQueryParameters = new QuoteDetailQueryParameters({
              quoteHeaderId: this.route.snapshot.queryParams.transactionId.toString(),
              serviceGroupId: serviceGroup,
            });
            return this.apiService
              .get<QuoteDetail[]>(`v/1/fixed-operations/quote-details`, {
                params: quoteDetailQueryParams,
              })
              .pipe(
                /**
                 * TODO: Need to improve logic here to ensure we grab the proper quote line to delete
                 */
                map((quoteLines: QuoteDetail[]) => {
                  const filteredQuoteLines = quoteLines.filter(
                    (quoteLine: QuoteDetail) =>
                      quoteLine.serviceGroupId === serviceGroup && quoteLine.sourceType !== 'DL' && !quoteLine.declined
                  );
                  return filteredQuoteLines[filteredQuoteLines.length - 1];
                }),
                take(1)
              );
          }
        })
      )

      .subscribe((caseToDelete) => {
        if (caseToDelete) {
          if (this.params.transactionType === 'workOrder') {
            this.serviceLaneFacade.deleteWorkOrderCaseFromServer(caseToDelete);
          } else if (this.params.transactionType === 'appointment') {
            this.serviceLaneFacade.deleteSelectedAppointmentCaseOnServer(caseToDelete);
          } else if (this.params.transactionType === 'quote') {
            this.serviceLaneFacade.deleteQuoteLineFromServer(caseToDelete);
          }
        }
      });

    this.declineMenuSub$ = this.serviceLaneFacade.declineServiceFromHyundaiMenu$
      .pipe(
        filter((declinedData) => declinedData.serviceGroup != null),
        tap((declinedData) => {
          this.serviceLaneFacade.disableHyundaiMenuRemoveServiceButton$.next(true);

          this.hyundaiMenuFacade.declineServiceForHyundaiMenu({
            id: declinedData.serviceGroup.workOrderLocationOpCode,
            declinedReasonId: declinedData.declinedReason,
          });
          if (this.params.transactionType === 'workOrder') {
            const declinedService: WorkorderDeclinedMpviCode = new WorkorderDeclinedMpviCode();
            declinedService.workOrderId = this.route.snapshot.queryParams.transactionId;
            declinedService.opCode = declinedData.serviceGroup.workOrderLocationOpCode
              ? declinedData.serviceGroup.workOrderLocationOpCode
              : declinedData.serviceGroup.id;
            declinedService.classificationId = 1;
            declinedService.printFlag = true;
            declinedService.declinedFromServiceSchedule = true;

            if (declinedData.declinedReason) {
              declinedService.declinedReasonId = declinedData.declinedReason;
            }

            this.serviceLaneFacade.createWorkOrderDeclinedOperationOnServer(declinedService);
          } else if (this.params.transactionType === 'appointment') {
            const declinedService: ServiceAppointmentDeclinedMpviCode = new ServiceAppointmentDeclinedMpviCode();
            declinedService.appointmentId = this.route.snapshot.queryParams.transactionId;
            declinedService.opCode = declinedData.serviceGroup.workOrderLocationOpCode;

            if (declinedData.declinedReason) {
              declinedService.declinedReason = declinedData.declinedReason;
            }

            this.serviceLaneFacade.createServiceAppointmentDeclinedMPVICode(declinedService);
          } else if (this.params.transactionType === 'quote') {
            this.serviceLaneFacade
              .activeQuoteLineWithServiceGroupExists(declinedData.serviceGroup.id)
              .pipe(take(1))
              .subscribe((activeQuoteLine) => {
                if (!activeQuoteLine) {
                  const quoteDetail: QuoteDetail = {
                    ...new QuoteDetail(),
                    rooftopId: this.authEmployee.rooftopId,
                    employeeId: this.authEmployee.associateId,
                    associateId: this.authEmployee.associateId,
                    declined: declinedData.declinedReason,
                    declinedDatetime: new Date(),
                    serviceGroupId: declinedData.serviceGroup.id,
                    serviceName: declinedData.serviceGroup.name,
                    labourOpCode: declinedData.serviceGroup.workOrderLocationOpCode,
                    recommendedHours: declinedData.serviceGroup.serviceGroupRateId
                      ? declinedData.serviceGroup.serviceGroupRate.recommendedHours != null
                        ? declinedData.serviceGroup.serviceGroupRate.recommendedHours
                        : declinedData.serviceGroup.recommendedHours
                      : declinedData.serviceGroup.recommendedHours,
                    technicianGroupId: declinedData.serviceGroup.technicianGroupId,
                    technicianGroupName: declinedData.serviceGroup.technicianGroupName,
                    technicianId: declinedData.serviceGroup.technicianId,
                    warrantySubmissionTypeId:
                      declinedData.serviceGroup.warrantySubmissionTypeId != null &&
                      declinedData.serviceGroup.warrantySubmissionTypeId.trim() != ''
                        ? declinedData.serviceGroup.warrantySubmissionTypeId
                        : 'CP',
                    rateType: declinedData.serviceGroup.serviceGroupRate
                      ? declinedData.serviceGroup?.serviceGroupRate?.rateType
                        ? declinedData?.serviceGroup?.serviceGroupRate?.rateType
                        : null
                      : null,
                    rate: declinedData.serviceGroup.serviceGroupRate
                      ? declinedData?.serviceGroup?.serviceGroupRate?.rate
                        ? declinedData?.serviceGroup?.serviceGroupRate?.rate
                        : null
                      : null,

                    shopSupplyFlag: declinedData?.serviceGroup?.serviceGroupRate
                      ? declinedData?.serviceGroup?.serviceGroupRate?.shopFlag
                        ? declinedData?.serviceGroup?.serviceGroupRate?.shopFlag
                        : null
                      : null,
                    totalAmount: declinedData?.serviceGroup?.serviceGroupRate
                      ? declinedData?.serviceGroup?.serviceGroupRate?.rate
                        ? declinedData?.serviceGroup?.serviceGroupRate?.rate
                        : null
                      : null,
                    lineAddedFromServiceSchedule: 1,
                    quoteHeaderId: this.params.transactionId,
                  };
                  (quoteDetail.excludeFromVcr = ['CP', 'P2', 'P3', 'P4'].includes(quoteDetail.warrantySubmissionTypeId)
                    ? false
                    : true),
                    (quoteDetail.expirationDate = new Date());
                  quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);
                  this.serviceLaneFacade.createQuoteLineOnServer(quoteDetail);
                } else {
                  /**
                   * If the line was previously accepted, switch to declined by updating declined fields
                   */
                  if (!activeQuoteLine.declined) {
                    activeQuoteLine.declined = declinedData.declinedReason;
                    activeQuoteLine.declinedDatetime = new Date();
                    this.serviceLaneFacade.updateQuoteLineOnServer(activeQuoteLine);
                  } else {
                    this.serviceLaneFacade.disableHyundaiMenuRemoveServiceButton$.next(false);
                  }
                }
              });
          }
        })
      )
      .subscribe();

    this.updateDeclinedServiceFromMenuSubscription$ = this.serviceLaneFacade.updateDeclinedServiceFromHyundaiMenu$
      .pipe(
        switchMap((updateData) => {
          if (this.params.transactionType === 'workOrder') {
            return this.apiService
              .get<WorkorderDeclinedMpviCode[]>(`v/1/fixed-operations/work-order-declined-mpvi-codes`, {
                params: { workOrderId: this.route.snapshot.queryParams.transactionId },
              })
              .pipe(
                map((workOrderDeclinedOperations) => {
                  return {
                    declinedOp: workOrderDeclinedOperations.filter(
                      (workOrderDeclinedOperation: any) =>
                        workOrderDeclinedOperation.opCode === updateData.serviceGroup.workOrderLocationOpCode
                    )[0],
                    declinedReason: updateData.declinedReason,
                  };
                }),
                take(1)
              );
          } else if (this.params.transactionType === 'appointment') {
            return this.apiService
              .get<ServiceAppointmentDeclinedMpviCode[]>(
                `v/1/fixed-operations/service-appointment-declined-mpvi-codes`,
                {
                  params: { appointmentId: this.route.snapshot.queryParams.transactionId },
                }
              )
              .pipe(
                map((appointmentCases) => {
                  return {
                    declinedOp: appointmentCases.filter(
                      (appointmentCase: any) =>
                        appointmentCase.opCode === updateData.serviceGroup.workOrderLocationOpCode
                    )[0],
                    declinedReason: updateData.declinedReason,
                  };
                }),
                take(1)
              );
          } else if (this.params.transactionType === 'quote') {
            const quoteDetailQueryParams: QuoteDetailQueryParameters = new QuoteDetailQueryParameters({
              quoteHeaderId: this.route.snapshot.queryParams.transactionId.toString(),
              serviceGroupId: updateData.serviceGroup.id,
            });
            return this.apiService
              .get<QuoteDetail[]>(`v/1/fixed-operations/quote-details`, {
                params: quoteDetailQueryParams,
              })
              .pipe(
                /**
                 * TODO: Need to improve logic here to ensure we grab the proper quote line to update
                 */
                map((quoteLines: QuoteDetail[]) => {
                  const filteredQuoteLines = quoteLines.filter(
                    (quoteLine: QuoteDetail) =>
                      quoteLine.serviceGroupId === updateData.serviceGroup.id &&
                      quoteLine.sourceType !== 'DL' &&
                      quoteLine.declined &&
                      quoteLine.declined > 0
                  );

                  return {
                    declinedOp: filteredQuoteLines[filteredQuoteLines.length - 1],
                    declinedReason: updateData.declinedReason,
                  };
                }),
                take(1)
              );
          }
        })
      )
      .subscribe((data) => {
        if (data.declinedOp) {
          if (this.params.transactionType === 'workOrder') {
            const declinedOpToUpdate = { ...data.declinedOp, declinedReasonId: data.declinedReason };
            this.serviceLaneFacade.updateWorkOrderDeclinedOperationOnServer(declinedOpToUpdate);
          } else if (this.params.transactionType === 'appointment') {
            const declinedOpToUpdate = { ...data.declinedOp, declinedReason: data.declinedReason };
            this.serviceLaneFacade.updateSelectedAppointmentDeclinedMpviOnServer(declinedOpToUpdate);
          } else if (this.params.transactionType === 'quote') {
            const declinedQuoteLineToUpdate = { ...data.declinedOp, declined: data.declinedReason };
            this.serviceLaneFacade.updateQuoteLineOnServer(declinedQuoteLineToUpdate);
          }
        }
      });

    this.removeDeclinedServiceFromMenuSubscription$ = this.serviceLaneFacade.removeDeclinedServiceFromHyundaiMenu$
      .pipe(
        switchMap((serviceGroup) => {
          this.hyundaiMenuFacade.removeDeclinedServiceFromHyundaiMenu(serviceGroup.workOrderLocationOpCode);
          if (this.params.transactionType === 'workOrder') {
            return this.apiService
              .get<WorkorderDeclinedMpviCode[]>(`v/1/fixed-operations/work-order-declined-mpvi-codes`, {
                params: { workOrderId: this.route.snapshot.queryParams.transactionId },
              })
              .pipe(
                map(
                  (workOrderCases) =>
                    workOrderCases.filter(
                      (workOrderCase: any) => workOrderCase.opCode === serviceGroup.workOrderLocationOpCode
                    )[0]
                ),
                take(1)
              );
          } else if (this.params.transactionType === 'appointment') {
            return this.apiService
              .get<ServiceAppointmentDeclinedMpviCode[]>(
                `v/1/fixed-operations/service-appointment-declined-mpvi-codes`,
                {
                  params: { appointmentId: this.route.snapshot.queryParams.transactionId },
                }
              )
              .pipe(
                map(
                  (appointmentCases) =>
                    appointmentCases.filter(
                      (appointmentCase: any) => appointmentCase.opCode === serviceGroup.workOrderLocationOpCode
                    )[0]
                ),
                take(1)
              );
          } else if (this.params.transactionType === 'quote') {
            const quoteDetailQueryParams: QuoteDetailQueryParameters = new QuoteDetailQueryParameters({
              quoteHeaderId: this.route.snapshot.queryParams.transactionId.toString(),
              serviceGroupId: serviceGroup.id,
            });
            return this.apiService
              .get<QuoteDetail[]>(`v/1/fixed-operations/quote-details`, {
                params: quoteDetailQueryParams,
              })
              .pipe(
                /**
                 * TODO: Need to improve logic here to ensure we grab the proper quote line to delete
                 */
                map((quoteLines: QuoteDetail[]) => {
                  const filteredQuoteLines = quoteLines.filter(
                    (quoteLine: QuoteDetail) =>
                      quoteLine.serviceGroupId === serviceGroup.id && quoteLine.sourceType !== 'DL'
                  );
                  return filteredQuoteLines[filteredQuoteLines.length - 1];
                }),
                take(1)
              );
          }
        })
      )

      .subscribe((caseToDelete) => {
        if (caseToDelete) {
          if (this.params.transactionType === 'workOrder') {
            this.serviceLaneFacade.deleteWorkOrderDeclinedOperationFromServer(caseToDelete);
          } else if (this.params.transactionType === 'appointment') {
            this.serviceLaneFacade.deleteSelectedAppointmentDeclinedMpviFromServer(caseToDelete);
          } else if (this.params.transactionType === 'quote') {
            this.serviceLaneFacade.deleteQuoteLineFromServer(caseToDelete);
          }
        }
      });
  }
}
